import { zodResolver } from "@hookform/resolvers/zod";
import { Check, Loader2, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";

import { ALERT_MAX_LENGTH } from "@handoverpro/shared";

import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Textarea } from "~/components/ui/textarea";
import type { TeamMemberDto } from "~/lib/api";
import { cn } from "~/lib/utils";

import {
  ALERT_COUNTER_THRESHOLD,
  patientFormSchema,
  SBAR_COUNTER_THRESHOLD,
  SBAR_MAX,
  type PatientFormValues,
} from "./schema";
import { usePatientFormDraft, type PatientFormDraftState } from "./use-patient-form-draft";

// Radix Select forbids an empty-string item value, so the "no doctor" choice
// uses this sentinel and maps back to "" in form state.
const UNASSIGNED = "__unassigned__";

type SbarField = "situation" | "background" | "assessment" | "recommendation";

// The four SBAR fields, each with the full name + one-line guidance the spec and
// the Medical Council mandate (E4.2).
const SBAR_FIELDS: readonly { name: SbarField; label: string; guidance: string }[] = [
  {
    name: "situation",
    label: "Situation",
    guidance: "What is happening with this patient right now",
  },
  {
    name: "background",
    label: "Background",
    guidance: "Relevant medical history, medications, test results",
  },
  { name: "assessment", label: "Assessment", guidance: "What you think is going on clinically" },
  {
    name: "recommendation",
    label: "Recommendation",
    guidance: "Exactly what the next doctor needs to do for this patient",
  },
];

const SBAR_INCOMPLETE_HINT = "Fill in all four SBAR sections to save";

// Inert onSave for when auto-save is disabled (no saver supplied / read-only).
// The hook is called unconditionally to respect the rules of hooks, but its
// `enabled: false` means this never actually runs.
const rejectSave = (): Promise<boolean> => Promise.resolve(false);

// Task urgency levels (E4.3), ordered most → least urgent. Plain text labels
// double as each option's screen-reader name (no dot decoration to shadow).
const TASK_URGENCY_OPTIONS: readonly {
  value: PatientFormValues["tasks"][number]["urgency"];
  label: string;
}[] = [
  { value: "urgent", label: "Urgent" },
  { value: "soon", label: "Soon" },
  { value: "routine", label: "Routine" },
];

const HIGH_PRIORITY_NEEDS_TASK_HINT = "High priority patients must have at least one task";

// Priority options (E4.5). A coloured dot makes the level obvious at a glance;
// `srLabel` is the screen-reader phrasing ("High priority") rendered as visually
// hidden text, since the dot and short label are decorative (see the item below).
// Order is High → Medium → Low so the safety default (High) is the first option.
const PRIORITY_OPTIONS: readonly {
  value: PatientFormValues["priority"];
  label: string;
  srLabel: string;
  dotClass: string;
}[] = [
  { value: "high", label: "High", srLabel: "High priority", dotClass: "bg-red-500" },
  { value: "medium", label: "Medium", srLabel: "Medium priority", dotClass: "bg-amber-500" },
  { value: "low", label: "Low", srLabel: "Low priority", dotClass: "bg-green-500" },
];

const ROLE_LABEL: Record<TeamMemberDto["role"], string> = {
  consultant: "Consultant",
  registrar: "Registrar",
  sho: "SHO",
  fy1: "FY1",
};

export interface PatientFormProps {
  mode: "create" | "edit";
  defaultValues: PatientFormValues;
  teamMembers: TeamMemberDto[];
  onSubmit: (values: PatientFormValues) => Promise<void>;
  onCancel: () => void;
  /** Locked session or acknowledged patient → fields disabled, no submit. */
  readOnly?: boolean;
  /** A server-side error to surface above the actions (e.g. a 400 or 403). */
  serverError?: string | null;
  /**
   * Persists the current form values as an auto-saved draft (HAN-72). When
   * provided (and not read-only), the form saves every 60s and on blur and shows
   * a "draft saved" indicator. Resolves `true` when the snapshot persisted.
   * Omitted → no auto-save (e.g. a read-only form).
   */
  autoSaveDraft?: (values: PatientFormValues) => Promise<boolean>;
}

/**
 * The shared Add/Edit patient form (E4). Built on react-hook-form + zod +
 * shadcn `<Form>`, validating on blur and on submit. E4.1 renders the
 * basic-details section; SBAR (E4.2), Tasks (E4.3), Alert (E4.4) and the
 * Priority dropdown (E4.5) slot in as additional <Section>s below, each reading
 * from the same form via `form.control`.
 */
export function PatientForm({
  mode,
  defaultValues,
  teamMembers,
  onSubmit,
  onCancel,
  readOnly = false,
  serverError = null,
  autoSaveDraft,
}: PatientFormProps): JSX.Element {
  const form = useForm<PatientFormValues>({
    resolver: zodResolver(patientFormSchema),
    defaultValues,
    mode: "onBlur",
  });

  const isSubmitting = form.formState.isSubmitting;

  // Auto-save draft (HAN-72). Independent of the Save gating below: a draft saves
  // even while SBAR is incomplete. Disabled on a read-only form or when no
  // saver is supplied.
  const draft = usePatientFormDraft(form, {
    enabled: !readOnly && autoSaveDraft != null,
    onSave: autoSaveDraft ?? rejectSave,
  });

  // SBAR is mandatory: Save stays disabled until all four fields are non-empty
  // after trimming. This reflects current values immediately (not on blur), so a
  // whitespace-only field keeps Save greyed out. The resolver and the API mirror
  // the same rule; this is the always-visible gate the spec requires.
  const sbar = form.watch("sbar");
  const sbarComplete = SBAR_FIELDS.every((f) => sbar[f.name].trim().length > 0);

  // Tasks (E4.3). `useFieldArray` owns the add/remove/reorder identity; rows stay
  // in insertion order (drag-reorder is out of scope for v1).
  const taskRows = useFieldArray({ control: form.control, name: "tasks" });

  // The conditional rule: a High-priority patient must carry at least one task.
  // Evaluated against live values (not the resolver) so the gate reflects every
  // keystroke and lifts the instant priority drops below High. The API and the
  // shared schema enforce the same rule authoritatively.
  const priority = form.watch("priority");
  const tasks = form.watch("tasks");
  const highPriorityNeedsTask = priority === "high" && tasks.length === 0;

  // Focus the description of a freshly-added row. The append re-renders before
  // the new input registers, so defer the focus to the following commit.
  const [rowToFocus, setRowToFocus] = useState<number | null>(null);
  useEffect(() => {
    if (rowToFocus === null) return;
    form.setFocus(`tasks.${rowToFocus}.description`);
    setRowToFocus(null);
  }, [rowToFocus, form]);

  function addTask(): void {
    setRowToFocus(taskRows.fields.length);
    taskRows.append({ description: "", urgency: "routine" });
  }

  // Save is blocked until SBAR is complete and the High-priority task rule holds.
  // The first unmet condition supplies the tooltip on the disabled button.
  const saveBlocked = !sbarComplete || highPriorityNeedsTask;
  const saveBlockedHint = !sbarComplete
    ? SBAR_INCOMPLETE_HINT
    : highPriorityNeedsTask
      ? HIGH_PRIORITY_NEEDS_TASK_HINT
      : undefined;

  // Stop a stray Enter in a single-line text input from implicitly submitting
  // the form. Textareas keep Enter (newlines) and the Save button still submits
  // on click or when it is the focused element.
  function handleKeyDown(event: React.KeyboardEvent<HTMLFormElement>): void {
    const target = event.target as HTMLElement;
    if (event.key === "Enter" && target.tagName === "INPUT") {
      event.preventDefault();
    }
  }

  return (
    <Form {...form}>
      <form
        noValidate
        onSubmit={form.handleSubmit(onSubmit)}
        onKeyDown={handleKeyDown}
        // Form-level blur (focusout bubbles) auto-saves a draft when a field
        // loses focus; the hook no-ops if nothing changed (HAN-72).
        onBlur={draft.flush}
        className="space-y-8"
      >
        <fieldset disabled={readOnly} className="space-y-8 disabled:opacity-90">
          <Section
            title="Patient details"
            description="Who this handover is about and where to find them."
          >
            <FormField
              control={form.control}
              name="hospitalNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Hospital number</FormLabel>
                  <FormControl>
                    <Input autoComplete="off" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="fullName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Patient name</FormLabel>
                  <FormControl>
                    <Input autoComplete="off" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="dateOfBirth"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Date of birth</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="bedNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Bed number</FormLabel>
                  <FormControl>
                    <Input autoComplete="off" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="diagnosis"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Diagnosis</FormLabel>
                  <FormControl>
                    <Textarea rows={3} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="assignedDoctorId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Assign to doctor</FormLabel>
                  <Select
                    value={field.value === "" ? UNASSIGNED : field.value}
                    onValueChange={(value) => field.onChange(value === UNASSIGNED ? "" : value)}
                    disabled={readOnly}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Unassigned" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                      {teamMembers.map((member) => (
                        <SelectItem key={member.id} value={member.id}>
                          {member.fullName} · {ROLE_LABEL[member.role]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>Optional — a doctor on this ward&rsquo;s shift.</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </Section>

          <Section
            title="SBAR Handover (all fields required)"
            description="The structured handover the Medical Council mandates. Save stays disabled until all four are complete."
          >
            {SBAR_FIELDS.map((sbarField) => (
              <FormField
                key={sbarField.name}
                control={form.control}
                name={`sbar.${sbarField.name}`}
                render={({ field }) => {
                  const length = field.value.length;
                  const showCounter = length >= SBAR_MAX - SBAR_COUNTER_THRESHOLD;
                  return (
                    <FormItem>
                      <FormLabel>{sbarField.label}</FormLabel>
                      <FormDescription>{sbarField.guidance}</FormDescription>
                      <FormControl>
                        <Textarea rows={3} maxLength={SBAR_MAX} {...field} />
                      </FormControl>
                      <div className="flex items-start justify-between gap-2">
                        <FormMessage />
                        {showCounter ? (
                          <span
                            className="ml-auto shrink-0 text-xs tabular-nums text-muted-foreground"
                            aria-live="polite"
                          >
                            {length} / {SBAR_MAX}
                          </span>
                        ) : null}
                      </div>
                    </FormItem>
                  );
                }}
              />
            ))}
          </Section>

          <Section
            title="Tasks for the next doctor"
            description="Specific jobs the incoming team should pick up. A High-priority patient needs at least one."
          >
            {taskRows.fields.length > 0 ? (
              <ol className="space-y-4">
                {taskRows.fields.map((row, index) => {
                  const position = index + 1;
                  return (
                    <li key={row.id} className="flex flex-col gap-3 sm:flex-row sm:items-start">
                      <FormField
                        control={form.control}
                        name={`tasks.${index}.description`}
                        render={({ field }) => (
                          <FormItem className="flex-1">
                            <FormLabel className="sr-only">Task {position} description</FormLabel>
                            <FormControl>
                              <Input
                                autoComplete="off"
                                placeholder="Describe the task"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name={`tasks.${index}.urgency`}
                        render={({ field }) => (
                          <FormItem className="sm:w-40">
                            <FormLabel className="sr-only">Task {position} urgency</FormLabel>
                            <Select
                              value={field.value}
                              onValueChange={field.onChange}
                              disabled={readOnly}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {TASK_URGENCY_OPTIONS.map((option) => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="shrink-0 text-muted-foreground hover:text-destructive"
                        onClick={() => taskRows.remove(index)}
                        aria-label={`Remove task ${position}`}
                      >
                        <Trash2 />
                      </Button>
                    </li>
                  );
                })}
              </ol>
            ) : (
              <p className="text-sm text-muted-foreground">No tasks yet.</p>
            )}
            <Button type="button" variant="outline" size="sm" onClick={addTask}>
              <Plus />
              Add task
            </Button>
          </Section>

          <Section
            title="Alert"
            description="An optional one-line warning, shown in red, for the most urgent issues."
          >
            <FormField
              control={form.control}
              name="alert"
              render={({ field }) => {
                const length = field.value.length;
                const showCounter = length >= ALERT_MAX_LENGTH - ALERT_COUNTER_THRESHOLD;
                return (
                  <FormItem>
                    <FormLabel>Alert (optional)</FormLabel>
                    <FormControl>
                      {/* Single-line on purpose (no textarea): enforces one line
                          and lets mobile keyboards show the simpler "done" key. */}
                      <Input
                        type="text"
                        autoComplete="off"
                        maxLength={ALERT_MAX_LENGTH}
                        placeholder="Hb dropping — check bloods urgently"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      For critically urgent one-line warnings only. Will appear in red on the
                      patient card.
                    </FormDescription>
                    <div className="flex items-start justify-between gap-2">
                      <FormMessage />
                      {showCounter ? (
                        <span
                          className="ml-auto shrink-0 text-xs tabular-nums text-muted-foreground"
                          aria-live="polite"
                        >
                          {length} / {ALERT_MAX_LENGTH}
                        </span>
                      ) : null}
                    </div>
                  </FormItem>
                );
              }}
            />
          </Section>

          <Section
            title="Priority"
            description="How urgently the incoming team should review this patient."
          >
            <FormField
              control={form.control}
              name="priority"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Priority</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange} disabled={readOnly}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {PRIORITY_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {/* Radix names the item (and the trigger it mirrors into)
                              from this text, so the dot + short label are aria-hidden
                              and the full "High priority" phrase carries the name. */}
                          <span className="flex items-center gap-2">
                            <span
                              aria-hidden="true"
                              className={cn("h-2.5 w-2.5 rounded-full", option.dotClass)}
                            />
                            <span aria-hidden="true">{option.label}</span>
                            <span className="sr-only">{option.srLabel}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Defaults to High. Change only if the patient is genuinely lower priority.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </Section>
        </fieldset>

        {serverError ? (
          <Alert variant="destructive">
            <AlertTitle>Couldn&rsquo;t save</AlertTitle>
            <AlertDescription>{serverError}</AlertDescription>
          </Alert>
        ) : null}

        <div className="flex flex-wrap items-center gap-3">
          {!readOnly ? (
            // The button itself carries `disabled:pointer-events-none`, so a
            // disabled button swallows hover — the wrapping span owns the
            // not-allowed cursor and the tooltip the spec asks for.
            <span
              className={saveBlocked ? "cursor-not-allowed" : undefined}
              title={saveBlockedHint}
            >
              <Button type="submit" disabled={isSubmitting || saveBlocked}>
                {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {mode === "create" ? "Add patient" : "Save changes"}
              </Button>
            </span>
          ) : null}
          <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
            {readOnly ? "Back" : "Cancel"}
          </Button>
          {!readOnly && autoSaveDraft != null ? <DraftIndicator state={draft} /> : null}
        </div>
      </form>
    </Form>
  );
}

/**
 * The "draft saved" indicator the spec asks for (HAN-72). Shows "Saving…" while
 * a save is in flight and "Draft saved · HH:MM" once one has landed; renders
 * nothing before the first save. `aria-live="polite"` announces the transitions
 * without stealing focus.
 */
function DraftIndicator({ state }: { state: PatientFormDraftState }): JSX.Element | null {
  const { status, lastSavedAt } = state;

  let content: JSX.Element | null = null;
  if (status === "saving") {
    content = (
      <>
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
        Saving&hellip;
      </>
    );
  } else if (lastSavedAt) {
    const time = lastSavedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    content = (
      <>
        <Check className="h-3.5 w-3.5 text-green-600" aria-hidden="true" />
        Draft saved &middot; {time}
      </>
    );
  }

  return (
    <span
      className="ml-auto inline-flex items-center gap-1.5 text-xs text-muted-foreground"
      aria-live="polite"
    >
      {content}
    </span>
  );
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section className="space-y-4">
      <div className="space-y-1">
        <h2 className="text-lg font-medium">{title}</h2>
        {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
      </div>
      <div className="space-y-5">{children}</div>
    </section>
  );
}
